var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, FlexBox, Icon, Spinner, Switch, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { usePrevious } from 'react-use';
import { change } from 'redux-form';
import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { messengerAppearanceFormName } from '../../../messengerSettingsFormValueSelector';
import { messengerSettingsFormFields } from '../../../types';
import { resolveLogoUrl } from '../../../utils';
import { FormSectionTitle } from '../../formSectionTitle';
import styles from './styles.module.scss';
export const MessengerLogo = ({ logo }) => {
    const dispatch = useAppDispatch();
    const [isEnabled, setIsEnabled] = useState(logo !== undefined);
    const [isUploading, setIsUploading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const notify = useNotifications();
    const messengerApi = useDixaMessengerApi();
    // When the logo form value is cleared, the switch should be toggled on/off
    // based on the initial value of the form field.
    const previousLogo = usePrevious(logo);
    useEffect(() => {
        if (!previousLogo && logo) {
            setIsEnabled(true);
        }
        if (previousLogo && !logo) {
            setIsEnabled(false);
        }
    }, [logo, previousLogo]);
    const fileInput = useRef(null);
    const openFilePicker = useCallback(() => {
        var _a;
        if (isUploading)
            return;
        (_a = fileInput.current) === null || _a === void 0 ? void 0 : _a.click();
    }, [isUploading]);
    const clearSelectedFile = useCallback(() => {
        dispatch(change(messengerAppearanceFormName, messengerSettingsFormFields.logo, null));
    }, [dispatch]);
    const handleSwitchToggled = useCallback(({ target: { checked } }) => {
        if (!checked && isEnabled) {
            clearSelectedFile();
        }
        setIsEnabled(checked);
    }, [clearSelectedFile, isEnabled]);
    const uploadFile = useCallback((file) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setHasError(false);
            setIsUploading(true);
            const { id: fileId } = yield messengerApi.uploadLogo(file);
            dispatch(change(messengerAppearanceFormName, messengerSettingsFormFields.logo, fileId));
        }
        catch (error) {
            setHasError(true);
            notify({
                title: 'Upload failed. Please try again.',
                type: 'error',
            });
        }
        finally {
            setIsUploading(false);
        }
    }), [dispatch, messengerApi, notify]);
    const handleFileUpload = useCallback(({ target }) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (target instanceof HTMLInputElement) {
            const file = (_b = (_a = target.files) === null || _a === void 0 ? void 0 : _a.item(0)) !== null && _b !== void 0 ? _b : null;
            if (!file) {
                clearSelectedFile();
            }
            else {
                uploadFile(file);
            }
        }
    }), [clearSelectedFile, uploadFile]);
    const handleTryAgain = useCallback(() => {
        var _a, _b, _c;
        const file = (_c = (_b = (_a = fileInput.current) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b.item(0)) !== null && _c !== void 0 ? _c : null;
        if (file) {
            uploadFile(file);
        }
    }, [uploadFile]);
    const previewLogo = useMemo(() => resolveLogoUrl(logo), [logo]);
    return (_jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "400" }, { children: [_jsx(FormSectionTitle, { title: "Logo", description: "Upload a logo", rightSlot: _jsx(Switch, { name: "logo", value: "", checked: isEnabled, onChange: handleSwitchToggled }) }), isEnabled && (_jsxs(FlexBox, Object.assign({ gap: "300", alignItems: "center" }, { children: [_jsx(Box, Object.assign({ className: styles.uploadIcon, onClick: openFilePicker }, { children: previewLogo ? (_jsx("img", { src: previewLogo, alt: "Logo Preview", className: styles.previewImage })) : (_jsx(Icon, { name: "image", size: 16 })) })), _jsx(Button, Object.assign({ variant: "secondary", onClick: openFilePicker, disabled: isUploading }, { children: _jsxs(_Fragment, { children: [_jsxs(FlexBox, Object.assign({ justifyContent: "center" }, { children: [_jsx(Spinner, { size: 16, className: classNames(styles.loadingSpinner, {
                                                [styles.loadingSpinnerShown]: isUploading,
                                            }) }), _jsx(Box, Object.assign({ className: classNames({
                                                [styles.uploadTextHidden]: isUploading,
                                            }) }, { children: "Upload new file" }))] })), _jsx("input", { ref: fileInput, type: "file", accept: "image/png", "data-testid": "upload-input", onChange: handleFileUpload, className: styles.fileUpload })] }) })), hasError && (_jsx(Button, Object.assign({ variant: "secondary", onClick: handleTryAgain, disabled: isUploading }, { children: "Try again" })))] })))] })));
};
